import { Controller, Control, UseFormSetValue } from "react-hook-form";

import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { ListRecurrence, ListType, SmartListFrequency } from "@/enum/list.enum";

import { ListRunEmailToggle } from "../../../components/ListRunEmailToggle";
import { SaveListSettings } from "../../../schema/list";

interface ListRunScheduleSectionProps {
	control: Control<SaveListSettings>;
	setValue: UseFormSetValue<SaveListSettings>;
	formValues?: Partial<SaveListSettings>;
	data?: { id?: number; type?: ListType; recurrence?: ListRecurrence } | null;
	isSmartList: boolean;
	blockListFrequency: boolean;
	blockListFrequencyInfo?: string;
	frequencyOptions: DropdownAutoCompleteItem[];
	tsSettings: (key: string) => string;
}

/**
 * How often the list runs, and whether this user wants an email each time it does.
 *
 * A smart list picks a frequency directly; every other list picks one-off vs ongoing and, when
 * ongoing, a recurrence. The run-email toggle sits underneath because it is the same question
 * from the user's side — "how often does this list bother me" (PREC-3908).
 *
 * Extracted from `ListEditSetting.tsx`, which is over the file-length ratchet and may only shrink.
 */
export const ListRunScheduleSection = ({
	control,
	setValue,
	formValues,
	data,
	isSmartList,
	blockListFrequency,
	blockListFrequencyInfo,
	frequencyOptions,
	tsSettings,
}: ListRunScheduleSectionProps) => {
	return (
		<>
			{isSmartList ? (
				<div className="flex flex-row gap-4 w-full mb-8">
					<Controller
						control={control}
						name="smartList.frequency"
						render={({ field }) => (
							<Dropdown
								data={frequencyOptions}
								defaultValue={frequencyOptions.find((o) => o.id === field.value)}
								floating
								handleSelect={(item) => {
									field.onChange(item.id);
									setValue("smartList.frequency", item.id as SmartListFrequency, {
										shouldDirty: true,
										shouldValidate: true,
									});
								}}
								label={tsSettings("updateFrequency")}
							/>
						)}
					/>
				</div>
			) : (
				<div className="flex flex-row gap-4 w-full mb-8">
					<Dropdown
						data={[
							{
								title: tsSettings("once"),
								id: ListType.ONCE,
							},
							{
								title: tsSettings("ongoing"),
								id: ListType.ON_GOING,
							},
						]}
						disabled={blockListFrequency}
						floating={true}
						handleSelect={(value: DropdownAutoCompleteItem) => {
							if (value.id && value.id !== formValues?.type) {
								if (value.id === ListType.ONCE) {
									setValue("maxDate", null, {
										shouldDirty: true,
										shouldValidate: true,
									});
								}

								setValue("type", value.id as ListType, {
									shouldDirty: true,
									shouldValidate: true,
								});
							}
						}}
						information={blockListFrequencyInfo}
						label={tsSettings("listType")}
						value={
							formValues?.type
								? {
										id: formValues.type,
										title: tsSettings(formValues.type === ListType.ONCE ? "once" : "ongoing"),
									}
								: undefined
						}
					/>

					{formValues?.type === ListType.ON_GOING && (
						<Dropdown
							data={[
								{ title: tsSettings("recurrenceDaily"), id: ListRecurrence.DAILY },
								{ title: tsSettings("recurrenceWeekly"), id: ListRecurrence.WEEKLY },
								{ title: tsSettings("recurrenceMonthly"), id: ListRecurrence.MONTHLY },
							]}
							floating={true}
							handleSelect={(value: DropdownAutoCompleteItem) => {
								if (value.id && value.id !== formValues?.recurrence) {
									setValue("recurrence", value.id as ListRecurrence, {
										shouldDirty: true,
										shouldValidate: true,
									});
								}
							}}
							label={tsSettings("recurrenceLabel")}
							value={
								formValues?.recurrence
									? {
											id: formValues.recurrence,
											title: tsSettings(
												`recurrence${formValues.recurrence.charAt(0)}${formValues.recurrence
													.slice(1)
													.toLowerCase()}`
											),
										}
									: undefined
							}
						/>
					)}
				</div>
			)}

			{/* PREC-3908: the per-user run email, directly under the frequency controls it
					    describes. Personal setting, saved on toggle — not part of the list form. */}
			{!isSmartList && !!data?.id && (
				<ListRunEmailToggle
					listId={data.id}
					recurrence={formValues?.recurrence ?? data?.recurrence}
					type={formValues?.type ?? data?.type}
				/>
			)}
		</>
	);
};
